import React, { useMemo, useState, useEffect } from 'react';
import { GameState, Cup, CupFixture } from '../../domain/entities';
import { soundService } from '../../services/SoundService';
import { AiService } from '../../services/AiService';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { PlayerOfTheSeasonCard } from '../shared/PlayerOfTheSeasonCard';
import { TeamBadge } from '../shared/TeamBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faAward } from '@fortawesome/free-solid-svg-icons';
import './EndOfSeasonScreen.css';

interface EndOfSeasonScreenProps {
    gameState: GameState;
    onAdvance: () => void;
    isAiPunditEnabled?: boolean;
}

export const EndOfSeasonScreen = ({ gameState, onAdvance, isAiPunditEnabled }: EndOfSeasonScreenProps) => {
    const [isSharing, setIsSharing] = useState(false);
    const [activeMainTab, setActiveMainTab] = useState<'awards' | 'leagues' | 'cups'>('awards');
    const [activeAwardsSubTab, setActiveAwardsSubTab] = useState<'my-team' | 'league-awards'>('my-team');
    const [storyOfTheSeason, setStoryOfTheSeason] = useState<string | null>(null);
    const [isGeneratingStory, setIsGeneratingStory] = useState(false);

    const { config } = gameState;
    const currentLeagueConfig = config.LEAGUE_CONFIG;
    const currentPrizeMoney = config.PRIZE_MONEY;
    const currentLeagueNames = config.LEAGUE_NAMES;

    const playerTeam = gameState.leagues[gameState.playerLeagueName].teams.find(t => t.name === gameState.playerTeamName);
    const playerTeamRank = gameState.leagues[gameState.playerLeagueName].teams.findIndex(t => t.name === gameState.playerTeamName);
    const leagueConfig = currentLeagueConfig[gameState.playerLeagueName];

    // Check for achievements
    const wasPromoted = playerTeamRank < leagueConfig.promotion;
    const wasRelegated = playerTeamRank >= leagueConfig.teams - leagueConfig.relegation && leagueConfig.relegation > 0;
    const wonLeague = playerTeamRank === 0;

    // Check for cup wins
    const cupWins = useMemo(() => {
        return Object.values(gameState.cups).filter((cup: Cup) => cup.champion === gameState.playerTeamName);
    }, [gameState.cups, gameState.playerTeamName]);

    const playerOfTheSeason = useMemo(() => {
        if (!playerTeam) return null;

        // Use current season stats (not history, since it hasn't been written yet)
        const playersWithSeasonStats = playerTeam.squad.map(player => {
            return {
                ...player,
                gamesPlayed: player.gamesPlayed || 0,
                goalsScored: player.goalsScored || 0,
                assists: player.assists || 0,
                potmAwards: player.potmAwards || 0,
            };
        });

        return playersWithSeasonStats.sort((a, b) =>
            ((b.goalsScored * 2) + b.assists + (b.potmAwards * 3)) -
            ((a.goalsScored * 2) + a.assists + (a.potmAwards * 3))
        )[0] || null;
    }, [playerTeam]);

    // Top scorer for current team
    const topScorer = useMemo(() => {
        if (!playerTeam) return null;

        return playerTeam.squad
            .map(player => {
                return {
                    ...player,
                    goalsScored: player.goalsScored || 0,
                };
            })
            .filter(p => p.goalsScored > 0)
            .sort((a, b) => b.goalsScored - a.goalsScored)[0] || null;
    }, [playerTeam]);

    // Best average rating for current team
    const bestAvgRating = useMemo(() => {
        if (!playerTeam) return null;

        return playerTeam.squad
            .map(player => {
                // Calculate average rating from recent ratings
                const avgRating = player.recentRatings && player.recentRatings.length > 0
                    ? player.recentRatings.reduce((sum, r) => sum + r, 0) / player.recentRatings.length
                    : 0;
                return {
                    ...player,
                    averageRating: avgRating,
                    gamesPlayed: player.gamesPlayed || 0,
                };
            })
            .filter(p => p.gamesPlayed > 0 && p.averageRating > 0)
            .sort((a, b) => b.averageRating - a.averageRating)[0] || null;
    }, [playerTeam]);

    // League-wide awards (top scorer and most assists across all teams in player's league)
    const leagueAwards = useMemo(() => {
        const playerLeague = gameState.leagues[gameState.playerLeagueName];
        if (!playerLeague) return { topScorer: null, mostAssists: null };

        // Collect all players from all teams in the league
        const allPlayers = playerLeague.teams.flatMap(team =>
            team.squad.map(player => {
                return {
                    ...player,
                    teamName: team.name,
                    goalsScored: player.goalsScored || 0,
                    assists: player.assists || 0,
                };
            })
        );

        const topScorer = allPlayers
            .filter(p => p.goalsScored > 0)
            .sort((a, b) => b.goalsScored - a.goalsScored)[0] || null;

        const mostAssists = allPlayers
            .filter(p => p.assists > 0)
            .sort((a, b) => b.assists - a.assists)[0] || null;

        return { topScorer, mostAssists };
    }, [gameState.leagues, gameState.playerLeagueName]);

    // Generate story of the season if AI is enabled
    useEffect(() => {
        const generateStory = async () => {
            if (!isAiPunditEnabled || AiService.isAiAvailable() === false || !playerTeam) {
                return;
            }

            setIsGeneratingStory(true);
            try {
                const completedSeason = gameState.season - 1;
                let achievement = '';
                if (wonLeague) {
                    achievement = 'Won the league championship';
                } else if (wasPromoted) {
                    achievement = 'Secured promotion';
                } else if (wasRelegated) {
                    achievement = 'Fought against relegation';
                } else {
                    achievement = 'Maintained league status';
                }

                if (cupWins.length > 0) {
                    achievement += ` and won the ${cupWins.map(c => c.name).join(' and ')}`;
                }

                const seasonSummary = {
                    teamName: gameState.playerTeamName,
                    leagueName: gameState.playerLeagueName,
                    position: playerTeamRank + 1,
                    leagueSize: gameState.leagues[gameState.playerLeagueName].teams.length,
                    wins: playerTeam.won,
                    draws: playerTeam.drawn,
                    losses: playerTeam.lost,
                    goalsFor: playerTeam.goalsFor,
                    goalsAgainst: playerTeam.goalsAgainst,
                    cupPerformance: cupWins.length > 0 ? `Won ${cupWins.map(c => c.name).join(' and ')}` : 'No cup wins',
                    achievement: achievement,
                    objective: 'Achieve success and climb the leagues',
                };

                const story = await AiService.generateStoryOfTheSeason(seasonSummary);
                setStoryOfTheSeason(story);
            } catch (e) {
                console.error("Failed to generate story of the season:", e);
                setStoryOfTheSeason(null);
            } finally {
                setIsGeneratingStory(false);
            }
        };

        generateStory();
    }, [isAiPunditEnabled, playerTeam, playerTeamRank, gameState.season, gameState.playerLeagueName, gameState.playerTeamName, gameState.leagues, cupWins, wonLeague, wasPromoted, wasRelegated]);

    let playerMessage = '';
    let prizeMoney = 0;

    if (wonLeague) {
        playerMessage = `🏆 CHAMPIONS! Congratulations on winning ${gameState.playerLeagueName}!`;
        const prizeConfig = (currentPrizeMoney as any)[gameState.playerLeagueName];
        if (prizeConfig && 'championship' in prizeConfig) {
            prizeMoney = prizeConfig.championship;
        }
    } else if (wasPromoted) {
        playerMessage = `Congratulations on being promoted from ${gameState.playerLeagueName}!`;
        const prizeConfig = (currentPrizeMoney as any)[gameState.playerLeagueName];
        if (prizeConfig && 'promotion' in prizeConfig) {
            prizeMoney = prizeConfig.promotion;
        }
    } else if (wasRelegated) {
        playerMessage = `Unfortunately, the team has been relegated from ${gameState.playerLeagueName}.`;
    } else {
        playerMessage = `You finished ${playerTeamRank + 1}${getOrdinal(playerTeamRank + 1)} in ${gameState.playerLeagueName}, securing your place for next season.`;
    }

    const handleAdvance = () => {
        soundService.playAdvance();
        onAdvance();
    }

    const handleShare = async () => {
        setIsSharing(true);
        try {
            if (!playerOfTheSeason) return;

            await Promise.all([
                document.fonts.load("bold 1.4em 'Poppins', sans-serif"),
                document.fonts.load("1.8em 'Poppins', sans-serif"),
                document.fonts.load("1.5em 'Poppins', sans-serif"),
            ]);

            const docStyle = getComputedStyle(document.documentElement);
            const BG_GRADIENT_START = '#111';
            const BG_GRADIENT_END = '#333';
            const GOLD_COLOR = docStyle.getPropertyValue('--gold-color').trim();
            const TEXT_COLOR = '#FFFFFF';

            const canvas = document.createElement('canvas');
            canvas.width = 320;
            canvas.height = 450;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, BG_GRADIENT_START);
            gradient.addColorStop(1, BG_GRADIENT_END);
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.strokeStyle = GOLD_COLOR;
            ctx.lineWidth = 2;
            ctx.strokeRect(0, 0, canvas.width, canvas.height);

            ctx.textAlign = 'center';
            ctx.fillStyle = GOLD_COLOR;
            ctx.font = "bold 1.4em 'Poppins', sans-serif";
            ctx.fillText('PLAYER OF THE SEASON', canvas.width / 2, 35);
            ctx.fillStyle = TEXT_COLOR;
            ctx.globalAlpha = 0.8;
            ctx.font = "0.9em 'Poppins', sans-serif";
            ctx.fillText(`SEASON ${gameState.season}`, canvas.width / 2, 55);
            ctx.globalAlpha = 1.0;

            ctx.strokeStyle = GOLD_COLOR;
            ctx.beginPath();
            ctx.moveTo(20, 70);
            ctx.lineTo(canvas.width - 20, 70);
            ctx.stroke();

            ctx.fillStyle = 'rgba(255,255,255,0.1)';
            ctx.beginPath();
            ctx.arc(canvas.width / 2, 145, 64, 0, Math.PI * 2);
            ctx.fill();
            ctx.font = '90px sans-serif';
            ctx.filter = `hue-rotate(${playerOfTheSeason.hue}deg) saturate(1.5)`;
            ctx.fillText(playerOfTheSeason.faceEmoji, canvas.width / 2, 175);
            ctx.filter = 'none';

            ctx.fillStyle = TEXT_COLOR;
            ctx.font = "bold 1.8em 'Poppins', sans-serif";
            ctx.fillText(playerOfTheSeason.name, canvas.width / 2, 250);

            ctx.globalAlpha = 0.9;
            ctx.font = "1em 'Poppins', sans-serif";
            ctx.fillText(playerTeam!.name, canvas.width / 2, 275);
            ctx.globalAlpha = 1.0;

            ctx.fillStyle = GOLD_COLOR;
            ctx.font = "bold 1em 'Poppins', sans-serif";
            ctx.fillText(playerOfTheSeason.position, canvas.width / 2, 295);

            ctx.fillStyle = 'rgba(0,0,0,0.3)';
            ctx.fillRect(0, 330, canvas.width, canvas.height - 330);
            ctx.strokeStyle = GOLD_COLOR;
            ctx.beginPath();
            ctx.moveTo(0, 330);
            ctx.lineTo(canvas.width, 330);
            ctx.stroke();

            const stats = [
                { label: 'APPEARANCES', value: playerOfTheSeason.gamesPlayed },
                { label: 'GOALS', value: playerOfTheSeason.goalsScored },
                { label: 'ASSISTS', value: playerOfTheSeason.assists },
                { label: 'POTM', value: playerOfTheSeason.potmAwards },
            ];

            stats.forEach((stat, index) => {
                const x = (canvas.width / 4) * (index + 0.5);
                ctx.fillStyle = TEXT_COLOR;
                ctx.font = "bold 1.5em 'Poppins', sans-serif";
                ctx.fillText(String(stat.value), x, 370);
                ctx.fillStyle = TEXT_COLOR;
                ctx.globalAlpha = 0.8;
                ctx.font = "0.8em 'Poppins', sans-serif";
                ctx.fillText(stat.label, x, 390);
                ctx.globalAlpha = 1.0;
            });

            const link = document.createElement('a');
            link.download = `player-of-the-season-${playerOfTheSeason.name.replace(' ', '_')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();

        } catch (e) {
            console.error("Failed to share POTS card", e);
        } finally {
            setIsSharing(false);
        }
    };

    function getOrdinal(num: number): string {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) return 'st';
        if (j === 2 && k !== 12) return 'nd';
        if (j === 3 && k !== 13) return 'rd';
        return 'th';
    }

    return (
        <>
            <AdPlaceholder />
            <div className="main-content end-of-season">
                <h1>End of Season {gameState.season - 1}</h1>

                {/* Achievement Highlights */}
                <div style={{ marginBottom: '24px' }}>
                    {wonLeague && (
                        <div style={{
                            backgroundColor: 'var(--positive-color)',
                            color: 'var(--secondary-color)',
                            padding: '16px',
                            borderRadius: '4px',
                            marginBottom: '12px',
                            fontSize: '1.1em',
                            fontWeight: 'bold'
                        }}>
                            <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px', fontSize: '1.2em' }} />
                            LEAGUE CHAMPIONS!
                        </div>
                    )}
                    {wasPromoted && !wonLeague && (
                        <div style={{
                            backgroundColor: 'var(--info-color)',
                            color: 'var(--secondary-color)',
                            padding: '16px',
                            borderRadius: '4px',
                            marginBottom: '12px',
                            fontSize: '1.1em',
                            fontWeight: 'bold'
                        }}>
                            <FontAwesomeIcon icon={faAward} style={{ marginRight: '8px', fontSize: '1.2em' }} />
                            PROMOTION SECURED!
                        </div>
                    )}
                    {wasRelegated && (
                        <div style={{
                            backgroundColor: 'var(--negative-color)',
                            color: 'white',
                            padding: '16px',
                            borderRadius: '4px',
                            marginBottom: '12px',
                            fontSize: '1.1em',
                            fontWeight: 'bold'
                        }}>
                            <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px', fontSize: '1.2em' }} />
                            Relegated to {gameState.playerLeagueName}
                        </div>
                    )}
                    {cupWins.length > 0 && (
                        <div style={{
                            backgroundColor: 'var(--warning-color)',
                            color: 'var(--secondary-color)',
                            padding: '16px',
                            borderRadius: '4px',
                            marginBottom: '12px',
                            fontSize: '1.1em',
                            fontWeight: 'bold'
                        }}>
                            <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px', fontSize: '1.2em' }} />
                            CUP WINNERS: {cupWins.map(c => c.name).join(', ')}
                        </div>
                    )}
                </div>

                <p>{playerMessage}</p>
                {prizeMoney > 0 && (
                    <p className="positive" style={{ fontSize: '1.05em', fontWeight: 'bold' }}>
                        You have been awarded £{prizeMoney.toLocaleString()} in prize money!
                    </p>
                )}

                {/* Tabbed Interface */}
                <div className="tabs" style={{ marginTop: '32px', marginBottom: '24px' }}>
                    <button
                        className={`tab-button ${activeMainTab === 'awards' ? 'active' : ''}`}
                        onClick={() => setActiveMainTab('awards')}
                    >
                        Awards
                    </button>
                    <button
                        className={`tab-button ${activeMainTab === 'leagues' ? 'active' : ''}`}
                        onClick={() => setActiveMainTab('leagues')}
                    >
                        Leagues
                    </button>
                    <button
                        className={`tab-button ${activeMainTab === 'cups' ? 'active' : ''}`}
                        onClick={() => setActiveMainTab('cups')}
                    >
                        Cups
                    </button>
                </div>

                {/* AWARDS TAB */}
                {activeMainTab === 'awards' && (
                    <div>
                        {/* Story of the Season */}
                        {storyOfTheSeason && (
                            <div style={{
                                backgroundColor: 'var(--primary-color)',
                                border: `2px solid var(--accent-text-color)`,
                                borderRadius: '4px',
                                padding: '16px',
                                marginBottom: '24px',
                                fontSize: '0.95em',
                                lineHeight: '1.6'
                            }}>
                                <h3 style={{
                                    color: 'var(--accent-text-color)',
                                    marginTop: 0,
                                    marginBottom: '12px',
                                    fontSize: '1.1em'
                                }}>
                                    Story of the Season
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {storyOfTheSeason}
                                </p>
                            </div>
                        )}
                        {isGeneratingStory && (
                            <div style={{
                                backgroundColor: 'var(--primary-color)',
                                borderRadius: '4px',
                                padding: '16px',
                                marginBottom: '24px',
                                opacity: 0.6
                            }}>
                                <p style={{ margin: 0, fontStyle: 'italic' }}>Generating story of the season...</p>
                            </div>
                        )}

                        <div className="tabs" style={{ marginBottom: '20px' }}>
                            <button
                                className={`tab-button ${activeAwardsSubTab === 'my-team' ? 'active' : ''}`}
                                onClick={() => setActiveAwardsSubTab('my-team')}
                            >
                                My Team
                            </button>
                            <button
                                className={`tab-button ${activeAwardsSubTab === 'league-awards' ? 'active' : ''}`}
                                onClick={() => setActiveAwardsSubTab('league-awards')}
                            >
                                League Awards
                            </button>
                        </div>

                        {/* My Team Awards */}
                        {activeAwardsSubTab === 'my-team' && (
                            <div className="awards-grid">
                                {/* Player of the Season */}
                                {playerOfTheSeason && playerTeam && (
                                    <div className="award-card">
                                        <h3 style={{ color: 'var(--gold-color)', marginBottom: '12px' }}>
                                            <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px' }} />
                                            Player of the Season
                                        </h3>
                                        <PlayerOfTheSeasonCard
                                            player={playerOfTheSeason}
                                            teamName={playerTeam.name}
                                            season={gameState.season - 1}
                                            teamColor={playerTeam.colors.primary}
                                            gameState={gameState}
                                        />
                                        <button className="button" onClick={handleShare} disabled={isSharing} style={{ marginTop: '12px', width: '100%' }}>
                                            {isSharing ? 'Generating...' : 'Share Card'}
                                        </button>
                                    </div>
                                )}

                                {/* Top Scorer */}
                                {topScorer && (
                                    <div className="award-card">
                                        <h3 style={{ color: 'var(--accent-text-color)', marginBottom: '12px' }}>
                                            <FontAwesomeIcon icon={faAward} style={{ marginRight: '8px' }} />
                                            Top Scorer
                                        </h3>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{
                                                fontSize: '3em',
                                                marginBottom: '8px',
                                                filter: `hue-rotate(${topScorer.hue}deg) saturate(1.5)`
                                            }}>
                                                {topScorer.faceEmoji}
                                            </div>
                                            <p style={{ fontSize: '1.2em', fontWeight: 'bold', marginBottom: '4px' }}>
                                                {topScorer.name}
                                            </p>
                                            <p style={{ opacity: 0.8, marginBottom: '8px' }}>
                                                {topScorer.position}
                                            </p>
                                            <p style={{ fontSize: '1.5em', color: 'var(--accent-text-color)', fontWeight: 'bold' }}>
                                                {topScorer.goalsScored} Goals
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Best Average Rating */}
                                {bestAvgRating && (
                                    <div className="award-card">
                                        <h3 style={{ color: 'var(--info-color)', marginBottom: '12px' }}>
                                            <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px' }} />
                                            Best Average Rating
                                        </h3>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{
                                                fontSize: '3em',
                                                marginBottom: '8px',
                                                filter: `hue-rotate(${bestAvgRating.hue}deg) saturate(1.5)`
                                            }}>
                                                {bestAvgRating.faceEmoji}
                                            </div>
                                            <p style={{ fontSize: '1.2em', fontWeight: 'bold', marginBottom: '4px' }}>
                                                {bestAvgRating.name}
                                            </p>
                                            <p style={{ opacity: 0.8, marginBottom: '8px' }}>
                                                {bestAvgRating.position}
                                            </p>
                                            <p style={{ fontSize: '1.5em', color: 'var(--info-color)', fontWeight: 'bold' }}>
                                                {bestAvgRating.averageRating.toFixed(1)} Rating
                                            </p>
                                            <p style={{ opacity: 0.7, fontSize: '0.9em' }}>
                                                ({bestAvgRating.gamesPlayed} appearances)
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* League Awards */}
                        {activeAwardsSubTab === 'league-awards' && (
                            <div className="table-wrapper">
                                <p style={{ opacity: 0.7, marginBottom: '16px' }}>
                                    Top performers across {gameState.playerLeagueName}
                                </p>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Award</th>
                                            <th>Player</th>
                                            <th>Team</th>
                                            <th>Stats</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td><FontAwesomeIcon icon={faTrophy} style={{ marginRight: '4px', color: '#FFD700' }} />Top Scorer</td>
                                            <td>{leagueAwards.topScorer?.name || '-'}</td>
                                            <td>{leagueAwards.topScorer?.teamName || '-'}</td>
                                            <td>{leagueAwards.topScorer?.goalsScored || '-'} goals</td>
                                        </tr>
                                        <tr>
                                            <td>2</td>
                                            <td><FontAwesomeIcon icon={faAward} style={{ marginRight: '4px', color: '#C0C0C0' }} />Most Assists</td>
                                            <td>{leagueAwards.mostAssists?.name || '-'}</td>
                                            <td>{leagueAwards.mostAssists?.teamName || '-'}</td>
                                            <td>{leagueAwards.mostAssists?.assists || '-'} assists</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* LEAGUES TAB */}
                {activeMainTab === 'leagues' && (
                    <div>
                        {currentLeagueNames.map(name => {
                            const leagueConfig = currentLeagueConfig[name];
                            const teams = gameState.leagues[name].teams;
                            const playerLeagueTeamRank = teams.findIndex(t => t.name === gameState.playerTeamName);
                            const isPlayerLeague = name === gameState.playerLeagueName;

                            return (
                                <div key={name} style={{ marginBottom: '32px' }}>
                                    <h3 style={{ marginBottom: '12px' }}>{name}</h3>
                                    <div className="table-wrapper">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Team</th>
                                                    <th>P</th>
                                                    <th>W</th>
                                                    <th>D</th>
                                                    <th>L</th>
                                                    <th>GD</th>
                                                    <th>Pts</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {teams.map((team, index) => {
                                                    const gd = team.goalsFor - team.goalsAgainst;
                                                    let rowClassName = '';
                                                    if (index < leagueConfig.promotion) {
                                                        rowClassName = 'promoted';
                                                    } else if (index >= teams.length - leagueConfig.relegation && leagueConfig.relegation > 0) {
                                                        rowClassName = 'relegated';
                                                    }
                                                    const isPlayerTeam = isPlayerLeague && team.name === gameState.playerTeamName;

                                                    return (
                                                        <tr
                                                            key={team.name}
                                                            className={`${rowClassName} ${isPlayerTeam ? 'highlight' : ''}`}
                                                            style={{
                                                                backgroundColor: isPlayerTeam ? 'rgba(255,255,255,0.05)' : undefined
                                                            }}
                                                        >
                                                            <td className="text-secondary">{index + 1}</td>
                                                            <td><span className={isPlayerTeam ? 'player-team-name' : ''}>{team.badge && <TeamBadge badge={team.badge} color={team.colors.primary} size="sm" />}{team.name}</span></td>
                                                            <td className="text-secondary">{team.played}</td>
                                                            <td className="text-secondary">{team.won}</td>
                                                            <td className="text-secondary">{team.drawn}</td>
                                                            <td className="text-secondary">{team.lost}</td>
                                                            <td className={gd > 0 ? 'positive' : gd < 0 ? 'negative' : 'text-secondary'}>
                                                                {gd > 0 ? `+${gd}` : gd}
                                                            </td>
                                                            <td className="points-column">{team.points}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ fontSize: '0.85em', opacity: 0.6, marginTop: '8px' }}>
                                        {leagueConfig.promotion > 0 && <p>↑ Top {leagueConfig.promotion}: Promoted</p>}
                                        {leagueConfig.relegation > 0 && <p>↓ Bottom {leagueConfig.relegation}: Relegated</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* CUPS TAB */}
                {activeMainTab === 'cups' && (
                    <div>
                        {Object.values(gameState.cups).length > 0 ? (
                            Object.entries(gameState.cups).map(([cupKey, cup]: [string, Cup]) => {
                                const playerTeamWon = cup.champion === gameState.playerTeamName;
                                // Get cup fixtures from gameState.fixtures.cups (not from cup object)
                                const cupFixtures = Object.values(gameState.fixtures.cups?.[cupKey] || {}).flat() as CupFixture[];
                                const playerTeamInCup = cupFixtures.some(f => f.home === gameState.playerTeamName || f.away === gameState.playerTeamName);

                                return (
                                    <div key={cupKey} style={{ marginBottom: '32px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                            <h3 style={{ margin: 0 }}>{cup.name}</h3>
                                            {playerTeamWon && (
                                                <span style={{
                                                    backgroundColor: 'var(--warning-color)',
                                                    color: 'var(--secondary-color)',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85em',
                                                    fontWeight: 'bold'
                                                }}>
                                                    <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '4px' }} />
                                                    Champions
                                                </span>
                                            )}
                                        </div>

                                        {(() => {
                                            // Find the final match (latest match with a result)
                                            const finalMatch = cupFixtures
                                                .filter(f => f.result)
                                                .sort((a, b) => b.week - a.week)[0];

                                            // Determine the winner from the final match
                                            const cupWinner = finalMatch && finalMatch.result
                                                ? (finalMatch.result.homeScore > finalMatch.result.awayScore ? finalMatch.home : finalMatch.away)
                                                : cup.champion;

                                            // Show champion section if we found either a final match or have a champion set
                                            if (finalMatch?.result || cup.champion) {
                                                return (
                                                    <div style={{
                                                        backgroundColor: 'var(--primary-color)',
                                                        padding: '24px',
                                                        borderRadius: '8px',
                                                        marginBottom: '24px',
                                                        border: '2px solid var(--warning-color)',
                                                        boxShadow: '0 4px 12px rgba(255, 215, 0, 0.2)'
                                                    }}>
                                                        {/* Display final match if available */}
                                                        {finalMatch?.result ? (
                                                            <div>
                                                                <p style={{ margin: '0 0 16px 0', opacity: 0.8, fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                                                    Final Result
                                                                </p>
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: '1fr auto 1fr',
                                                                    gap: '12px',
                                                                    alignItems: 'center',
                                                                    marginBottom: '20px'
                                                                }}>
                                                                    {/* Home Team */}
                                                                    <div style={{
                                                                        textAlign: 'right',
                                                                        padding: '12px',
                                                                        backgroundColor: finalMatch.home === cupWinner ? 'rgba(255, 215, 0, 0.1)' : 'rgba(100, 100, 100, 0.05)',
                                                                        borderRadius: '4px',
                                                                        border: finalMatch.home === cupWinner ? '1px solid var(--warning-color)' : 'transparent'
                                                                    }}>
                                                                        <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.1em' }}>
                                                                            {finalMatch.home}
                                                                        </p>
                                                                    </div>

                                                                    {/* Score */}
                                                                    <div style={{
                                                                        textAlign: 'center',
                                                                        fontSize: '2em',
                                                                        fontWeight: 'bold',
                                                                        padding: '8px 16px',
                                                                        minWidth: '80px'
                                                                    }}>
                                                                        <div>{finalMatch.result.homeScore}</div>
                                                                        <div style={{ fontSize: '0.5em', opacity: 0.6, marginTop: '4px' }}>-</div>
                                                                        <div>{finalMatch.result.awayScore}</div>
                                                                    </div>

                                                                    {/* Away Team */}
                                                                    <div style={{
                                                                        textAlign: 'left',
                                                                        padding: '12px',
                                                                        backgroundColor: finalMatch.away === cupWinner ? 'rgba(255, 215, 0, 0.1)' : 'rgba(100, 100, 100, 0.05)',
                                                                        borderRadius: '4px',
                                                                        border: finalMatch.away === cupWinner ? '1px solid var(--warning-color)' : 'transparent'
                                                                    }}>
                                                                        <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.1em' }}>
                                                                            {finalMatch.away}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : null}

                                                        {/* Winner Badge */}
                                                        {cupWinner && (
                                                            <div style={{
                                                                backgroundColor: 'var(--warning-color)',
                                                                color: 'var(--secondary-color)',
                                                                padding: '12px',
                                                                borderRadius: '4px',
                                                                textAlign: 'center',
                                                                fontWeight: 'bold',
                                                                fontSize: '1.1em'
                                                            }}>
                                                                <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px', color: '#FFD700' }} />
                                                                {cupWinner} - Champions
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        {playerTeamInCup ? (
                                            <div className="table-wrapper">
                                                <table className="table" style={{ fontSize: '0.9em' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Round</th>
                                                            <th>Home</th>
                                                            <th>Score</th>
                                                            <th>Away</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {cupFixtures
                                                            .filter(f => f.home === gameState.playerTeamName || f.away === gameState.playerTeamName)
                                                            .sort((a, b) => a.week - b.week)
                                                            .map((fixture, idx) => (
                                                                <tr
                                                                    key={idx}
                                                                    className={fixture.home === gameState.playerTeamName || fixture.away === gameState.playerTeamName ? 'highlight' : ''}
                                                                >
                                                                    <td style={{ opacity: 0.8 }}>Week {Math.floor(fixture.week)}</td>
                                                                    <td>{fixture.home}</td>
                                                                    <td className="points-column">
                                                                        {fixture.result
                                                                            ? `${fixture.result.homeScore}-${fixture.result.awayScore}`
                                                                            : 'TBP'
                                                                        }
                                                                    </td>
                                                                    <td>{fixture.away}</td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <p style={{ opacity: 0.6, fontStyle: 'italic' }}>Your team did not participate in this cup.</p>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <p style={{ opacity: 0.6 }}>No cups available this season.</p>
                        )}
                    </div>
                )}

                <button className="button primary" onClick={handleAdvance}>
                    Continue to Next Season
                </button>
            </div>
        </>
    );
};
